import { useState, useEffect } from 'react';
import { X, ShoppingBag, Plus, Minus, Trash2, ArrowRight, Sparkles } from 'lucide-react';
import { cn } from '@/utils/cn';
import { useShop, FREE_DELIVERY_THRESHOLD } from '@/context/ShopContext';
import { Link, useNavigate } from 'react-router-dom';

export function Cart() {
  const [isOpen, setIsOpen] = useState(false);
  const { cart, cartTotal, cartCount, shipping, orderTotal, updateCartQuantity, removeFromCart } = useShop();
  const navigate = useNavigate();

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // shipping/orderTotal come from ShopContext so the cart, the checkout screen
  // and the submitted order can never disagree about the price.
  const total = orderTotal;

  const handleCheckout = () => {
    setIsOpen(false);
    navigate('/checkout');
  };

  return (
    <>
      {/* Cart Toggle Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="relative p-2 transition-colors"
        style={{ color: '#567D38' }}
        aria-label="Shopping cart"
      >
        <ShoppingBag className="w-5 h-5" />
        {cartCount > 0 && (
          <span 
            className="absolute -top-0.5 -right-0.5 w-5 h-5 text-white text-[10px] font-bold flex items-center justify-center rounded-full"
            style={{ background: '#BDD630', color: '#567D38' }}
          >
            {cartCount > 9 ? '9+' : cartCount}
          </span>
        )}
      </button>

      {/* Cart Overlay */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 animate-in fade-in"
            onClick={() => setIsOpen(false)}
          />

          {/* Cart Panel */}
          <div 
            className="fixed right-0 top-0 h-full w-full max-w-md shadow-2xl z-50 animate-in slide-in-from-right"
            style={{ background: '#F7F5EE' }}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 bg-white border-b" style={{ borderColor: '#e8e2d9' }}>
              <div className="flex items-center gap-3">
                <ShoppingBag className="w-6 h-6" style={{ color: '#567D38' }} />
                <h2 className="font-serif font-bold text-xl" style={{ color: '#1A2E12' }}>Your Cart</h2>
                <span 
                  className="px-2 py-0.5 text-sm font-medium rounded-full"
                  style={{ background: '#BDD630', color: '#567D38' }}
                >
                  {cartCount} items
                </span>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 transition-colors"
                style={{ color: '#6B7B6E' }}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {cart.length === 0 ? (
              <EmptyCart onClose={() => setIsOpen(false)} />
            ) : (
              <>
                {/* Cart Items */}
                <div className="flex-1 overflow-y-auto p-6" style={{ height: 'calc(100% - 320px)' }}>
                  {/* Free Shipping Progress */}
                  {cartTotal < FREE_DELIVERY_THRESHOLD && (
                    <div className="mb-6 p-4 rounded-xl bg-white">
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="w-4 h-4" style={{ color: '#BDD630' }} />
                        <p className="text-sm" style={{ color: '#1A2E12' }}>
                          Add <strong>R{(FREE_DELIVERY_THRESHOLD - cartTotal).toFixed(2)}</strong> more for FREE shipping!
                        </p>
                      </div>
                      <div className="h-2 bg-[#F7F5EE] rounded-full overflow-hidden">
                        <div
                          className="h-full transition-all duration-500 rounded-full"
                          style={{ 
                            width: `${Math.min((cartTotal / FREE_DELIVERY_THRESHOLD) * 100, 100)}%`,
                            background: '#BDD630'
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Cart Items */}
                  <div className="space-y-4">
                    {cart.map((item) => (
                      <div key={item.id} className="flex gap-4 p-4 bg-white rounded-xl">
                        {/* Product Image */}
                        <Link 
                          to={`/product/${item.id}`}
                          onClick={() => setIsOpen(false)}
                          className="w-20 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-[#F7F5EE]"
                        >
                          <img
                            src={item.images[0]}
                            alt={item.name}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              const img = e.target as HTMLImageElement;
                              img.style.display = 'none';
                              const box = img.parentElement;
                              if (box) {
                                box.style.background = 'linear-gradient(135deg, #567D38, #1a3d2a)';
                                box.innerHTML = '<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;font-size:20px">🌿</div>';
                              }
                            }}
                          />
                        </Link>

                        {/* Product Info */}
                        <div className="flex-1 min-w-0">
                          <Link 
                            to={`/product/${item.id}`}
                            onClick={() => setIsOpen(false)}
                            className="font-semibold text-sm hover:underline transition-colors"
                            style={{ color: '#1A2E12' }}
                          >
                            {item.name}
                          </Link>
                          <p className="font-bold mt-1" style={{ color: '#567D38' }}>
                            R{item.price.toFixed(2)}
                          </p>

                          {/* Quantity Controls */}
                          <div className="flex items-center gap-3 mt-2">
                            <div 
                              className="flex items-center rounded-lg border"
                              style={{ borderColor: '#e8e2d9' }}
                            >
                              <button
                                onClick={() => updateCartQuantity(item.id, item.quantity - 1)}
                                className="p-2 hover:bg-[#F7F5EE] transition-colors"
                                style={{ color: '#6B7B6E' }}
                              >
                                <Minus className="w-4 h-4" />
                              </button>
                              <span className="w-10 text-center font-medium" style={{ color: '#1A2E12' }}>
                                {item.quantity}
                              </span>
                              <button
                                onClick={() => updateCartQuantity(item.id, item.quantity + 1)}
                                className="p-2 hover:bg-[#F7F5EE] transition-colors"
                                style={{ color: '#6B7B6E' }}
                              >
                                <Plus className="w-4 h-4" />
                              </button>
                            </div>

                            <button
                              onClick={() => removeFromCart(item.id)}
                              className="p-2 transition-colors hover:text-red-500"
                              style={{ color: '#6B7B6E' }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {/* Item Total */}
                        <div className="text-right">
                          <p className="font-bold" style={{ color: '#1A2E12' }}>
                            R{(item.price * item.quantity).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Footer */}
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-white border-t" style={{ borderColor: '#e8e2d9' }}>
                  {/* Summary */}
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span style={{ color: '#6B7B6E' }}>Subtotal</span>
                      <span className="font-medium" style={{ color: '#1A2E12' }}>R{cartTotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span style={{ color: '#6B7B6E' }}>Shipping</span>
                      <span className={shipping === 0 ? 'font-medium' : ''} style={{ color: shipping === 0 ? '#567D38' : '#1A2E12' }}>
                        {shipping === 0 ? 'FREE' : `R${shipping.toFixed(2)}`}
                      </span>
                    </div>
                    <div className="flex justify-between text-lg font-bold pt-3 border-t" style={{ borderColor: '#e8e2d9' }}>
                      <span style={{ color: '#1A2E12' }}>Total</span>
                      <span style={{ color: '#567D38' }}>R{total.toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Checkout Button */}
                  <button 
                    onClick={handleCheckout}
                    className="w-full py-4 text-white font-bold uppercase tracking-wider transition-all hover:scale-[1.02] flex items-center justify-center gap-2 rounded-full"
                    style={{ background: '#567D38' }}
                  >
                    Checkout
                    <ArrowRight className="w-5 h-5" />
                  </button>

                  <p className="text-xs text-center mt-3" style={{ color: '#6B7B6E' }}>
                    Taxes calculated at checkout
                  </p>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </>
  );
}

function EmptyCart({ onClose }: { onClose: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center h-[calc(100%-100px)] p-6 text-center">
      <div 
        className="w-24 h-24 rounded-full flex items-center justify-center mb-6"
        style={{ background: '#F7F5EE' }}
      >
        <ShoppingBag className="w-12 h-12" style={{ color: '#D9B38C' }} />
      </div>
      <h3 className="font-serif font-bold text-xl mb-2" style={{ color: '#1A2E12' }}>Your cart is empty</h3>
      <p className="mb-6" style={{ color: '#6B7B6E' }}>Discover our handcrafted products made with African botanicals.</p>
      <Link
        to="/shop"
        onClick={onClose}
        className="px-8 py-3 rounded-full font-semibold text-white transition-all hover:scale-105"
        style={{ background: '#567D38' }}
      >
        Start Shopping
      </Link>
    </div>
  );
}

export default Cart;
